import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Beaker,
  Lightbulb,
  Bot,
  Glasses,
  Languages,
  CheckCircle2,
  XCircle,
  Monitor,
  Award,
  Users,
  Loader2,
} from 'lucide-react';
import { ComparisonBarChart, DistributionPieChart, StatCard } from '../../components/analytics';
import {
  useGetSamagamSpecialLabsDetailsQuery,
} from '../../services/kvsSamagamApi';



export default function SpecialLabsAnalysis() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'atl' | 'arvr' | 'robotics' | 'language'>('atl');
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);

  // ── ATL Labs: real API ────────────────────────────────────────────────
  const { data: atlApiData, isLoading: atlLoading } = useGetSamagamSpecialLabsDetailsQuery(
    { category: 'atllabs' },
    { skip: activeTab !== 'atl' },
  );

  // ── AR/VR Labs: real API ─────────────────────────────────────────────
  const { data: arvrApiData, isLoading: arvrLoading } = useGetSamagamSpecialLabsDetailsQuery(
    { category: 'arvrlab' },
    { skip: activeTab !== 'arvr' },
  );

  // ── Robotics & AI: real API ───────────────────────────────────────────
  const { data: roboticsApiData, isLoading: roboticsLoading } = useGetSamagamSpecialLabsDetailsQuery(
    { category: 'roboticsai' },
    { skip: activeTab !== 'robotics' },
  );

  // ── Language Labs: real API ───────────────────────────────────────────
  const { data: languageApiData, isLoading: languageLoading } = useGetSamagamSpecialLabsDetailsQuery(
    { category: 'languagelab' },
    { skip: activeTab !== 'language' },
  );

  // Map API school records → same shape as mock ATL rows so all helpers work unchanged
  const atlApiRows = atlApiData?.schools.map((s, i) => ({
    sn:          i + 1,
    regionName:  s.roName,
    schoolName:  s.schoolName,
    kvid:        String(s.schoolID),
    established: s.atlLab === 'Available' ? 'Yes' : 'No',
    fundedBy:    s.atlFundedBy !== 'N/A' ? s.atlFundedBy : '—',
    year:        '—',
    budgeted:    '0',
    expenditure: '0',
  })) ?? null;

  // Map AR/VR API school records → mock arvr row shape
  const arvrApiRows = arvrApiData?.schools.map((s, i) => ({
    sn:          i + 1,
    regionName:  s.roName,
    schoolName:  s.schoolName,
    kvid:        String(s.schoolID),
    established: s.arvrLab === 'Available' ? 'Yes' : 'No',
    fundedBy:    s.arvrFundedBy !== 'N/A' ? s.arvrFundedBy : '—',
    year:        '—',
    budgeted:    '0',
    expenditure: '0',
  })) ?? null;

  // Map Robotics API school records → mock robotics row shape
  const roboticsApiRows = roboticsApiData?.schools.map((s, i) => ({
    sn:          i + 1,
    regionName:  s.roName,
    schoolName:  s.schoolName,
    kvid:        String(s.schoolID),
    established: s.roboticsLab === 'Available' ? 'Yes' : 'No',
    fundedBy:    s.robotFundedBy !== 'N/A' ? s.robotFundedBy : '—',
    year:        '—',
    budgeted:    '0',
    expenditure: '0',
  })) ?? null;

  // Map Language API school records → mock language row shape
  const languageApiRows = languageApiData?.schools.map((s, i) => ({
    sn:          i + 1,
    regionName:  s.roName,
    schoolName:  s.schoolName,
    kvid:        String(s.schoolID),
    isFunctional: s.languageLabFunctional === 'Functional' ? 'Yes' : 'No',
    amcGiven:    s.amcCovered === '1' ? 'Yes' : 'No',
    computers:   s.totalComputers,
    software:    '—',
    outcome:     '—',
  })) ?? null;

  // Assemble data object solely from API rows
  const data = {
    tableData: {
      atl:      atlApiRows      || [],
      arvr:     arvrApiRows     || [],
      robotics: roboticsApiRows || [],
      language: languageApiRows || [],
    }
  };

  const tabs = [
    { id: 'atl', label: 'ATL Labs', icon: Lightbulb },
    { id: 'arvr', label: 'AR/VR Labs', icon: Glasses },
    { id: 'robotics', label: 'Robotics & AI', icon: Bot },
    { id: 'language', label: 'Language Labs', icon: Languages },
  ];

  // chart helpers
  const getChartData = () => {
    if (activeTab === 'language') {
      const rows = data.tableData.language;
      const regionMap: Record<string, { yes: number; no: number }> = {};
      rows.forEach((r: any) => {
        const region = r.regionName || 'Unknown';
        if (!regionMap[region]) regionMap[region] = { yes: 0, no: 0 };
        if (r.isFunctional === 'Yes') regionMap[region].yes++;
        else regionMap[region].no++;
      });
      return Object.entries(regionMap).map(([region, counts]) => ({
        category: region,
        pmshriCount: counts.yes,
        nonPmshriCount: counts.no,
        total: counts.yes + counts.no,
        pmshriPercentage: (counts.yes + counts.no) > 0 ? (counts.yes / (counts.yes + counts.no)) * 100 : 0,
      }));
    } else {
      const rows = data.tableData[activeTab as 'atl' | 'arvr' | 'robotics'];
      const regionMap: Record<string, { yes: number; no: number }> = {};
      rows.forEach((r: any) => {
        const region = r.regionName || 'Unknown';
        if (!regionMap[region]) regionMap[region] = { yes: 0, no: 0 };
        if (r.established === 'Yes') regionMap[region].yes++;
        else regionMap[region].no++;
      });
      return Object.entries(regionMap).map(([region, counts]) => ({
        category: region,
        pmshriCount: counts.yes,
        nonPmshriCount: counts.no,
        total: counts.yes + counts.no,
        pmshriPercentage: (counts.yes + counts.no) > 0 ? (counts.yes / (counts.yes + counts.no)) * 100 : 0,
      }));
    }
  };

  const getPieData = () => {
    if (activeTab === 'language') {
      const rows = data.tableData.language;
      const amcYes = rows.filter((r: any) => r.amcGiven === 'Yes').length;
      const amcNo = rows.length - amcYes;
      return [
        { name: 'AMC Given', value: amcYes },
        { name: 'AMC Not Given', value: amcNo },
      ];
    } else {
      const rows = data.tableData[activeTab as 'atl' | 'arvr' | 'robotics'];
      const totalBudget = rows.reduce((sum: number, r: any) => sum + parseInt(r.budgeted || '0'), 0);
      const totalExp = rows.reduce((sum: number, r: any) => sum + parseInt(r.expenditure || '0'), 0);
      return [
        { name: 'Budget', value: totalBudget },
        { name: 'Expenditure', value: totalExp },
      ];
    }
  };

  const getSummaryCards = () => {
    // ATL tab: use real API summary when available
    if (activeTab === 'atl' && atlApiData?.summary) {
      const s = atlApiData.summary;
      return [
        { title: 'Total Schools',  value: s.totalSchools,                    color: 'purple' as const, icon: <Beaker className="w-5 h-5" />,       subtitle: 'With ATL labs' },
        { title: 'ATL Available',  value: s.atlAvailable,                    color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Established' },
        { title: 'Not Available',  value: s.totalSchools - s.atlAvailable,   color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Yet to set up' },
        { title: 'PM-SHRI',        value: s.pmShri,                          color: 'blue'   as const, icon: <Award className="w-5 h-5" />,         subtitle: 'PM-SHRI schools' },
        { title: 'Non PM-SHRI',    value: s.nonPmShri,                       color: 'orange' as const, icon: <Users className="w-5 h-5" />,         subtitle: 'Non PM-SHRI schools' },
      ];
    }

    // AR/VR tab: use real API summary when available
    if (activeTab === 'arvr' && arvrApiData?.summary) {
      const s = arvrApiData.summary;
      return [
        { title: 'Total Schools',   value: s.totalSchools,                      color: 'purple' as const, icon: <Beaker className="w-5 h-5" />,       subtitle: 'With AR/VR labs' },
        { title: 'AR/VR Available', value: s.arvrAvailable,                     color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Established' },
        { title: 'Not Available',   value: s.totalSchools - s.arvrAvailable,    color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Yet to set up' },
        { title: 'PM-SHRI',         value: s.pmShri,                            color: 'blue'   as const, icon: <Award className="w-5 h-5" />,         subtitle: 'PM-SHRI schools' },
        { title: 'Non PM-SHRI',     value: s.nonPmShri,                         color: 'orange' as const, icon: <Users className="w-5 h-5" />,         subtitle: 'Non PM-SHRI schools' },
      ];
    }

    // Robotics tab: use real API summary when available
    if (activeTab === 'robotics' && roboticsApiData?.summary) {
      const s = roboticsApiData.summary;
      return [
        { title: 'Total Schools',      value: s.totalSchools,                             color: 'purple' as const, icon: <Beaker className="w-5 h-5" />,       subtitle: 'With Robotics labs' },
        { title: 'Robotics Available', value: s.roboticsAvailable,                        color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Established' },
        { title: 'Not Available',      value: s.totalSchools - s.roboticsAvailable,       color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Yet to set up' },
        { title: 'PM-SHRI',            value: s.pmShri,                                   color: 'blue'   as const, icon: <Award className="w-5 h-5" />,         subtitle: 'PM-SHRI schools' },
        { title: 'Non PM-SHRI',        value: s.nonPmShri,                                color: 'orange' as const, icon: <Users className="w-5 h-5" />,         subtitle: 'Non PM-SHRI schools' },
      ];
    }

    // Language tab: use real API summary when available
    if (activeTab === 'language' && languageApiData?.summary) {
      const s = languageApiData.summary;
      return [
        { title: 'Total Schools',   value: s.totalSchools,          color: 'purple' as const, icon: <Languages className="w-5 h-5" />,     subtitle: 'With language labs' },
        { title: 'Functional',      value: s.totalFunctionalLabs,   color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Currently operational' },
        { title: 'Non-Functional',  value: s.totalNonFunctionalLabs,color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Need attention' },
        { title: 'AMC Provided',    value: s.amcProvided,           color: 'blue'   as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Maintenance covered' },
        { title: 'Total Computers', value: s.totalComputersInLabs,  color: 'orange' as const, icon: <Monitor className="w-5 h-5" />,      subtitle: 'Across all labs' },
      ];
    }

    // Fallback for any tab without API data yet
    if (activeTab === 'language') {
      const rows = data.tableData.language;
      const functional = rows.filter((r: any) => r.isFunctional === 'Yes').length;
      const amcYes = rows.filter((r: any) => r.amcGiven === 'Yes').length;
      const totalComputers = rows.reduce((sum: number, r: any) => sum + (r.computers || 0), 0);
      return [
        { title: 'Total Labs',      value: rows.length,              color: 'purple' as const, icon: <Languages className="w-5 h-5" />,     subtitle: 'Language labs' },
        { title: 'Functional',      value: functional,               color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Currently operational' },
        { title: 'Non-Functional',  value: rows.length - functional, color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Need attention' },
        { title: 'AMC Given',       value: amcYes,                   color: 'blue'   as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Maintenance covered' },
        { title: 'Total Computers', value: totalComputers,           color: 'orange' as const, icon: <Monitor className="w-5 h-5" />,      subtitle: 'Across all labs' },
      ];
    } else {
      const rows = data.tableData[activeTab as 'atl' | 'arvr' | 'robotics'];
      const established = rows.filter((r: any) => r.established === 'Yes').length;
      const label = activeTab === 'atl' ? 'ATL' : activeTab === 'arvr' ? 'AR/VR' : 'Robotics';
      return [
        { title: 'Total Labs',      value: rows.length,                color: 'purple' as const, icon: <Beaker className="w-5 h-5" />,       subtitle: `${label} labs` },
        { title: 'Established',     value: established,                color: 'green'  as const, icon: <CheckCircle2 className="w-5 h-5" />, subtitle: 'Operational' },
        { title: 'Not Established', value: rows.length - established,  color: 'pink'   as const, icon: <XCircle className="w-5 h-5" />,      subtitle: 'Yet to set up' },
        { title: 'PM-SHRI',         value: 0,                          color: 'blue'   as const, icon: <Award className="w-5 h-5" />,         subtitle: 'PM-SHRI schools' },
        { title: 'Non PM-SHRI',     value: 0,                          color: 'orange' as const, icon: <Users className="w-5 h-5" />,         subtitle: 'Non PM-SHRI schools' },
      ];
    }
  };


  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Beaker className="w-7 h-7" />
              Special Labs Analysis
            </h1>
            <p className="text-gray-600">
              ATL Labs, AR/VR, Robotics/AI and Digital Language Labs
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>



        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {getSummaryCards().map(card => (
            <StatCard
              key={card.title}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              subtitle={card.subtitle}
            />
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                    ? 'text-purple-700 border-purple-600 bg-purple-50'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>



        {/* Loading spinner */}
        {(
          (activeTab === 'atl'      && atlLoading)      ||
          (activeTab === 'arvr'     && arvrLoading)     ||
          (activeTab === 'robotics' && roboticsLoading) ||
          (activeTab === 'language' && languageLoading)
        ) && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        )}

        {!(
          (activeTab === 'atl'      && atlLoading)      ||
          (activeTab === 'arvr'     && arvrLoading)     ||
          (activeTab === 'robotics' && roboticsLoading) ||
          (activeTab === 'language' && languageLoading)
        ) && (
          <>
        {/* Charts for comparison */}
        <div className={`grid grid-cols-1 ${activeTab === 'language' ? 'lg:grid-cols-2' : ''} gap-6 mb-6`}>
          <ComparisonBarChart
            data={getChartData()}
            title="Status overview"
            xAxisLabel=""
          />
          {activeTab === 'language' && (
            <DistributionPieChart
              data={getPieData()}
              title="AMC distribution"
              colors={['#22c55e', '#ef4444']}
            />
          )}
        </div>

        {/* Detailed Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {tabs.find(t => t.id === activeTab)?.label} Details
          </h3>
          <div className="overflow-auto h-96">
            {activeTab === 'language' ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Sn</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Region Name</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">School Name</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">KVID</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Is Lab Functional</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">AMC Given</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">No. of Computer</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Software Installed</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.tableData.language.map((row: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => row.kvid && setSelectedSchool({ schoolID: parseInt(row.kvid), schoolName: row.schoolName, infraData: { schoolID: parseInt(row.kvid), schoolName: row.schoolName, region: row.regionName }, tabLabel: 'Language Lab' })}>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.sn}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.regionName}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.schoolName}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.kvid}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.isFunctional === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {row.isFunctional}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.amcGiven}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.computers}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.software}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.outcome}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Sn</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Region Name</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">School Name</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">KVID</th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">
                      {activeTab === 'atl' ? 'ATL Lab Established' : activeTab === 'arvr' ? 'AR/VR Lab Established' : 'Robotics/AI Lab Established'}
                    </th>
                    <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Funded By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.tableData[activeTab as 'atl' | 'arvr' | 'robotics'].map((row: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => row.kvid && setSelectedSchool({ schoolID: parseInt(row.kvid), schoolName: row.schoolName, infraData: { schoolID: parseInt(row.kvid), schoolName: row.schoolName, region: row.regionName, tabLabel: activeTab.toUpperCase() }, tabLabel: `${activeTab.toUpperCase()} Lab` })}>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.sn}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.regionName}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.schoolName}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.kvid}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.established === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {row.established}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{row.fundedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>



          </>
        )}

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'Special Labs';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${isCurrentPage
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-lg cursor-pointer hover:scale-105'
                    }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
